import { GoogleLogo, X } from "@phosphor-icons/react";
import { useState } from "react";
import { authConfigured, useAuth } from "../lib/auth";
import { track } from "../lib/analytics";

export function AuthModal({ open, onClose, onAuthenticated }: { open: boolean; onClose(): void; onAuthenticated(): void }) {
  const { user, signInGoogle, signInEmail, register, logout } = useAuth();
  const [mode, setMode] = useState<"signin" | "register">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  async function run(action: () => Promise<void>, authenticated = false, method?: "google" | "email" | "register") {
    setBusy(true);
    setMessage("");
    try {
      await action();
      if (authenticated) {
        track(method === "register" ? "sign_up" : "login", { method: method === "google" ? "google" : "email" });
        onAuthenticated();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message.replace(/^Firebase: /, "") : "Authentication failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="modal-close" onClick={onClose} aria-label="Close"><X size={20} /></button>
        {user ? (
          <>
            <p className="kicker">Your account</p>
            <h2 id="auth-title">Good to see you.</h2>
            <p>{user.email}</p>
            {!user.emailVerified && <p className="form-message">Verify your email to unlock three removals per day.</p>}
            <button className="primary-button full" onClick={() => run(logout)}>Sign out</button>
          </>
        ) : (
          <>
            <p className="kicker">Three removals daily</p>
            <h2 id="auth-title">{mode === "signin" ? "Sign in to KleanBG" : "Create your account"}</h2>
            <p className="modal-intro">Your images stay private. An account only stores your quota and plan.</p>
            {!authConfigured && <p className="form-message error">Authentication will be available when Firebase setup is complete.</p>}
            <button className="google-button" disabled={busy || !authConfigured} onClick={() => run(signInGoogle, true, "google")}>
              <GoogleLogo size={20} weight="bold" /> Continue with Google
            </button>
            <div className="or"><span>or</span></div>
            <form onSubmit={(event) => { event.preventDefault(); void run(() => mode === "signin" ? signInEmail(email, password) : register(email, password), true, mode === "signin" ? "email" : "register"); }}>
              <label>Email<input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
              <label>Password<input type="password" minLength={8} autoComplete={mode === "signin" ? "current-password" : "new-password"} required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
              {message && <p className="form-message error">{message}</p>}
              <button className="primary-button full" disabled={busy || !authConfigured}>{busy ? "Working..." : mode === "signin" ? "Sign in" : "Create account"}</button>
            </form>
            <button className="text-button" onClick={() => { setMode(mode === "signin" ? "register" : "signin"); setMessage(""); }}>
              {mode === "signin" ? "New here? Create an account" : "Already registered? Sign in"}
            </button>
          </>
        )}
      </section>
    </div>
  );
}
